import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Spinner, Alert, Button } from "reactstrap";
import MetaTags from "react-meta-tags";
import "../Findings/CentralFindings.scss";
import "./brandProtection.scss";

const STATUS_LABELS = {
  new: "New",
  investigating: "Investigating",
  takedown_requested: "Takedown requested",
  taken_down: "Taken down",
  dismissed: "Dismissed (false positive)",
};
// Reuse the status pill colours of the Findings page
const STATUS_PILL = {
  new: "st-new",
  investigating: "st-triaged",
  takedown_requested: "st-validated",
  taken_down: "st-remediated",
  dismissed: "st-falsepositive",
};
const LEVEL_LABELS = { high: "High", medium: "Medium", low: "Low" };
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 3 * 60 * 1000;

function formatDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ageText(value) {
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (!Number.isFinite(days) || days < 0) return "";
  if (days < 1) return "today";
  if (days < 60) return `${days} day${days === 1 ? "" : "s"} ago`;
  if (days < 730) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

const ConfidenceBar = ({ value, level }) => (
  <div className="brand-conf">
    <div className="brand-conf-track">
      <div className={`brand-conf-fill is-${level}`} style={{ width: `${Math.max(4, Math.min(100, value))}%` }} />
    </div>
    <span className="brand-conf-num">{value}</span>
  </div>
);

const Signals = ({ item }) => (
  <div className="brand-signals">
    {item.dns && item.dns.a && item.dns.a.length > 0 && <span className="brand-chip is-live">Live</span>}
    {item.http && item.http.hasLoginForm && <span className="brand-chip is-danger">Login form</span>}
    {item.registration && item.registration.date && Date.now() - new Date(item.registration.date).getTime() < 30 * 86400000 && (
      <span className="brand-chip is-warn">New</span>
    )}
  </div>
);

const BrandProtection = () => {
  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "read_only").toLowerCase();
  const canWrite = role === "super_admin" || role === "tenant_admin" || role === "analyst";
  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "dark" });
  const theme = layoutMode === "light" ? "light" : "dark";
  const apiBase = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [scanning, setScanning] = useState(false);
  const pollTimer = useRef(null);
  const pollStarted = useRef(0);

  const [editingWatch, setEditingWatch] = useState(false);
  const [watchForm, setWatchForm] = useState({ domains: "", keywords: "", excludeDomains: "", minConfidence: 40 });
  const [savingWatch, setSavingWatch] = useState(false);

  const [selected, setSelected] = useState(null); // list row
  const [detail, setDetail] = useState(null); // { case, related }
  const [statusVal, setStatusVal] = useState("");
  const [noteVal, setNoteVal] = useState("");
  const [ownerVal, setOwnerVal] = useState("");
  const [dueVal, setDueVal] = useState("");
  const [saving, setSaving] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}brand/summary`, { headers: headers() });
      if (res.status === 404) {
        setUnavailable(true);
        return null;
      }
      if (!res.ok) return null;
      const body = await res.json();
      if (body && body.status) {
        setSummary(body);
        return body;
      }
    } catch (e) {
      // keep the page usable
    }
    return null;
  }, [apiBase, headers]);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter || "all", limit: String(limit), offset: String((page - 1) * limit) });
      if (levelFilter) params.set("level", levelFilter);
      if (search) params.set("q", search);
      const res = await fetch(`${apiBase}brand/cases?${params.toString()}`, { headers: headers() });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.status) {
        setItems(body.items || []);
        setTotal(body.total || 0);
      }
    } catch (e) {
      setFeedback({ type: "danger", message: "Could not load look-alike domains." });
    } finally {
      setLoading(false);
    }
  }, [apiBase, headers, statusFilter, levelFilter, search, page, limit]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const showFilter = (level, status) => {
    setLevelFilter(level);
    setStatusFilter(status);
    setPage(1);
  };

  useEffect(() => {
    loadSummary().then((s) => {
      if (s && s.scanning) {
        setScanning(true);
        startPolling();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => () => clearTimeout(pollTimer.current), []);

  const poll = useCallback(async () => {
    const s = await loadSummary();
    if (s && !s.scanning) {
      setScanning(false);
      loadCases();
      return;
    }
    if (Date.now() - pollStarted.current > POLL_MAX_MS) {
      setScanning(false);
      return;
    }
    pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [loadSummary, loadCases]);

  function startPolling() {
    clearTimeout(pollTimer.current);
    pollStarted.current = Date.now();
    pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
  }

  const handleScan = async () => {
    setFeedback(null);
    setScanning(true);
    try {
      const res = await fetch(`${apiBase}brand/scan`, { method: "POST", headers: headers() });
      const body = await res.json().catch(() => ({}));
      if (res.status === 202 || res.status === 409) {
        startPolling();
        return;
      }
      setScanning(false);
      if (res.status === 429) {
        const when = body.nextScanAllowedAt ? new Date(body.nextScanAllowedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
        setFeedback({ type: "info", message: `Brand protection was scanned recently.${when ? ` You can scan again after ${when}.` : ""}` });
      } else {
        setFeedback({ type: "danger", message: body.error || "Could not start the scan." });
      }
    } catch (e) {
      setScanning(false);
      setFeedback({ type: "danger", message: "Could not start the scan." });
    }
  };

  const openWatchEditor = () => {
    const w = (summary && summary.watchlist) || {};
    setWatchForm({
      domains: (w.source === "custom" ? w.domains : []).join(", "),
      keywords: (w.keywords || []).join(", "),
      excludeDomains: (w.excludeDomains || []).join(", "),
      minConfidence: w.minConfidence || 40,
    });
    setEditingWatch(true);
  };

  const saveWatchlist = async () => {
    setSavingWatch(true);
    try {
      const res = await fetch(`${apiBase}brand/watchlist`, {
        method: "PUT",
        headers: headers(),
        body: JSON.stringify({
          domains: watchForm.domains,
          keywords: watchForm.keywords,
          excludeDomains: watchForm.excludeDomains,
          minConfidence: Number(watchForm.minConfidence),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFeedback({ type: "danger", message: body.error || "Could not save the watchlist." });
      } else {
        setEditingWatch(false);
        setFeedback({ type: "success", message: "Watchlist saved. Press Scan now to check it." });
        loadSummary();
      }
    } catch (e) {
      setFeedback({ type: "danger", message: "Could not save the watchlist." });
    } finally {
      setSavingWatch(false);
    }
  };

  const openDrawer = async (row) => {
    setSelected(row);
    setDetail(null);
    setNoteVal("");
    try {
      const res = await fetch(`${apiBase}brand/cases/${row._id}`, { headers: headers() });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.status) {
        setDetail(body);
        setStatusVal(body.case.status);
        setOwnerVal(body.case.owner || "");
        setDueVal(body.case.dueDate ? new Date(body.case.dueDate).toISOString().slice(0, 10) : "");
      }
    } catch (e) {
      setFeedback({ type: "danger", message: "Could not load the case." });
    }
  };

  const closeDrawer = () => {
    setSelected(null);
    setDetail(null);
  };

  const patchCase = async (changes, okMessage) => {
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}brand/cases/${selected._id}`, { method: "PATCH", headers: headers(), body: JSON.stringify(changes) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFeedback({ type: "danger", message: body.error || "Could not update the case." });
        return;
      }
      setDetail((d) => ({ ...(d || {}), case: body.case }));
      setStatusVal(body.case.status);
      setNoteVal("");
      setFeedback({ type: "success", message: okMessage });
      loadCases();
      loadSummary();
    } catch (e) {
      setFeedback({ type: "danger", message: "Could not update the case." });
    } finally {
      setSaving(false);
    }
  };

  const downloadEvidence = async () => {
    try {
      const res = await fetch(`${apiBase}brand/cases/${selected._id}/evidence`, { headers: headers() });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `brand-evidence-${selected.domain}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setFeedback({ type: "danger", message: "Could not download the evidence package." });
    }
  };

  if (unavailable) {
    return (
      <div className="page-content findings-page-root brand-page" data-theme={theme}>
        <div className="findings-container">
          <div className="findings-main-card" style={{ padding: "32px", textAlign: "center", color: "var(--fg-secondary)" }}>
            Brand protection is not available on this server yet.
          </div>
        </div>
      </div>
    );
  }

  const c = detail && detail.case;
  const counts = (summary && summary.counts) || { high: 0, medium: 0, low: 0, total: 0, open: 0, newThisWeek: 0, dismissed: 0 };
  const watch = (summary && summary.watchlist) || { domains: [], keywords: [], excludeDomains: [], source: "assets", minConfidence: 40 };
  const nextAllowed = summary && summary.nextScanAllowedAt ? new Date(summary.nextScanAllowedAt) : null;
  const coolingDown = nextAllowed && nextAllowed > new Date();
  const hasFilters = Boolean(levelFilter || search || statusFilter !== "open");
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="page-content findings-page-root brand-page" data-theme={theme}>
      <MetaTags>
        <title>Brand Protection | GuardLogiX</title>
      </MetaTags>

      <div className="findings-container">
        <div className="findings-header">
          <div className="findings-breadcrumbs">
            <Link to="/dashboard">Dashboard</Link>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-active">Brand Protection</span>
          </div>
          <div className="findings-title-row">
            <div>
              <h1 className="findings-title">Brand Protection</h1>
              <p className="findings-subtitle">
                Look-alike and typosquatting domains that imitate your brand, with evidence and a case workflow for takedown requests
              </p>
            </div>
            <div className="findings-header-actions">
              {canWrite && (
                <button
                  className="btn-refresh"
                  onClick={handleScan}
                  disabled={scanning || coolingDown}
                  title={coolingDown ? `Scanned recently. Available again after ${nextAllowed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Look for new look-alike domains now"}
                >
                  {scanning ? <Spinner size="sm" /> : null} {scanning ? "Scanning…" : "Scan now"}
                </button>
              )}
            </div>
          </div>
        </div>

        {feedback && (
          <Alert color={feedback.type} isOpen={true} toggle={() => setFeedback(null)} style={{ borderRadius: "var(--radius-md)", margin: 0 }}>
            {feedback.message}
          </Alert>
        )}

        {/* Watchlist */}
        <div className="findings-main-card brand-watch">
          <div className="brand-watch-head">
            <div>
              <div className="drawer-section-title" style={{ margin: 0 }}>Watchlist</div>
              <div className="brand-muted">
                {watch.source === "assets" ? "Using your organization's own domains." : "Custom list."} Checked once a day{summary && summary.lastScanAt ? `, last on ${formatDateTime(summary.lastScanAt)}` : ", not scanned yet"}.
                {canWrite && " Scan now runs the same check right away instead of waiting for the daily one (it takes a minute or two, and can be used once an hour)."}
              </div>
              {summary && summary.lastScanSummary && summary.lastScanSummary.error && (
                <div className="brand-warning">Last scan could not run: {summary.lastScanSummary.error}</div>
              )}
            </div>
            {canWrite && !editingWatch && (
              <button className="btn-refresh" onClick={openWatchEditor}>
                Edit watchlist
              </button>
            )}
          </div>

          {!editingWatch ? (
            <div className="brand-watch-body">
              <div className="brand-watch-row">
                <span className="brand-watch-k">Protected domains</span>
                <span>
                  {watch.domains.length ? watch.domains.map((d) => <span className="brand-chip" key={d}>{d}</span>) : <span className="brand-muted">None yet. Add your brand domains.</span>}
                </span>
              </div>
              {watch.keywords.length > 0 && (
                <div className="brand-watch-row">
                  <span className="brand-watch-k">Keywords</span>
                  <span>{watch.keywords.map((k) => <span className="brand-chip" key={k}>{k}</span>)}</span>
                </div>
              )}
              {watch.excludeDomains.length > 0 && (
                <div className="brand-watch-row">
                  <span className="brand-watch-k">Never report</span>
                  <span>{watch.excludeDomains.map((k) => <span className="brand-chip" key={k}>{k}</span>)}</span>
                </div>
              )}
              <div className="brand-watch-row">
                <span className="brand-watch-k">Open a finding from confidence</span>
                <span>{watch.minConfidence} / 100</span>
              </div>
            </div>
          ) : (
            <div className="brand-watch-form">
              <label>
                Protected domains (comma separated, up to 5). Leave empty to use your organization's own domains.
                <input value={watchForm.domains} onChange={(e) => setWatchForm({ ...watchForm, domains: e.target.value })} placeholder="example.com, example.in" />
              </label>
              <label>
                Brand keywords (up to 10), combined with login, secure, support and similar words
                <input value={watchForm.keywords} onChange={(e) => setWatchForm({ ...watchForm, keywords: e.target.value })} placeholder="examplebank, exbank" />
              </label>
              <label>
                Domains to never report (your own or partner domains)
                <input value={watchForm.excludeDomains} onChange={(e) => setWatchForm({ ...watchForm, excludeDomains: e.target.value })} placeholder="example-partner.com" />
              </label>
              <label>
                Minimum confidence to raise a finding (10 to 95)
                <input type="number" min="10" max="95" value={watchForm.minConfidence} onChange={(e) => setWatchForm({ ...watchForm, minConfidence: e.target.value })} />
              </label>
              <div className="mgmt-actions">
                <Button color="primary" size="sm" disabled={savingWatch} onClick={saveWatchlist}>
                  {savingWatch ? <Spinner size="sm" /> : "Save watchlist"}
                </Button>
                <Button color="secondary" outline size="sm" disabled={savingWatch} onClick={() => setEditingWatch(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* KPI cards */}
        <div className="findings-kpi-grid">
          <div className="kpi-card" onClick={() => showFilter("", "all")}>
            <div className="kpi-left">
              <span className="kpi-label">Look-alikes found</span>
              <span className="kpi-value">{counts.total}</span>
              <span className="kpi-desc">Registered domains that resemble your brand (dismissed ones not counted)</span>
            </div>
            <div className="kpi-icon-wrap kpi-open">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
          </div>
          <div className="kpi-card card-high" onClick={() => showFilter("high", "all")}>
            <div className="kpi-left">
              <span className="kpi-label">High confidence</span>
              <span className="kpi-value" style={{ color: counts.high > 0 ? "#ea580c" : "inherit" }}>{counts.high}</span>
              <span className="kpi-desc">Score 70 or more: most likely a real phishing or impersonation risk</span>
            </div>
            <div className="kpi-icon-wrap kpi-high">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
          </div>
          <div className="kpi-card" onClick={() => showFilter("", "new")}>
            <div className="kpi-left">
              <span className="kpi-label">New this week</span>
              <span className="kpi-value">{counts.newThisWeek}</span>
              <span className="kpi-desc">First found by a scan in the last 7 days</span>
            </div>
            <div className="kpi-icon-wrap kpi-open">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
          </div>
          <div className="kpi-card" onClick={() => showFilter("", "open")}>
            <div className="kpi-left">
              <span className="kpi-label">Open cases</span>
              <span className="kpi-value">{counts.open}</span>
              <span className="kpi-desc">Not closed yet: new, investigating or takedown requested</span>
            </div>
            <div className="kpi-icon-wrap kpi-overdue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="findings-main-card">
          <div className="findings-toolbar">
            <div className="toolbar-left">
              <div className="search-input-wrap">
                <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input type="text" placeholder="Search look-alike domain..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
                {searchInput && (
                  <button className="clear-search-btn" onClick={() => setSearchInput("")} title="Clear search">
                    ✕
                  </button>
                )}
              </div>
              <select className="filter-select" value={levelFilter} onChange={(e) => { setLevelFilter(e.target.value); setPage(1); }}>
                <option value="">All confidence levels</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select className="filter-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="open">Open cases</option>
                <option value="all">All except dismissed</option>
                <option value="new">New</option>
                <option value="investigating">Investigating</option>
                <option value="takedown_requested">Takedown requested</option>
                <option value="taken_down">Taken down</option>
                <option value="dismissed">Dismissed ({counts.dismissed})</option>
              </select>
              {hasFilters && (
                <button className="reset-filters-btn" onClick={() => { setSearchInput(""); setSearch(""); showFilter("", "open"); }}>
                  Reset Filters
                </button>
              )}
            </div>
          </div>

          <div className="findings-table-wrap">
            <table className="findings-table">
              <thead>
                <tr>
                  <th style={{ width: "150px" }}>Confidence</th>
                  <th style={{ minWidth: "240px" }}>Look-alike domain</th>
                  <th style={{ width: "190px" }}>Imitates</th>
                  <th style={{ width: "170px" }}>Technique</th>
                  <th style={{ width: "170px" }}>Registered</th>
                  <th style={{ width: "130px" }}>Signals</th>
                  <th style={{ width: "150px" }}>Status</th>
                  <th style={{ width: "150px" }}>Owner</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "40px" }}>
                      <Spinner color="primary" />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "48px 20px" }}>
                      <div style={{ color: "var(--fg-secondary)", fontSize: "14px", fontWeight: "600" }}>
                        {scanning ? "Scanning for look-alike domains…" : "No look-alike domains to show"}
                      </div>
                      <div style={{ color: "var(--fg-tertiary)", fontSize: "12px", marginTop: "4px" }}>
                        {hasFilters
                          ? "Try clearing your filters."
                          : summary && summary.lastScanAt
                          ? "The last scan found nothing that needs attention."
                          : "Press Scan now to check your brand domains for look-alikes."}
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item._id} className={selected && selected._id === item._id ? "selected-row" : ""} onClick={() => openDrawer(item)}>
                      <td>
                        <div className="brand-conf-cell">
                          <span className={`sev-badge sev-${item.level}`}>
                            <span className="dot" />
                            {LEVEL_LABELS[item.level] || item.level}
                          </span>
                          <ConfidenceBar value={item.confidence} level={item.level} />
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: "var(--fg-primary)", overflowWrap: "anywhere" }}>{item.displayDomain || item.domain}</div>
                        {item.displayDomain && item.displayDomain !== item.domain && <div className="brand-muted brand-mono">{item.domain}</div>}
                      </td>
                      <td>
                        <div className="asset-cell" title={item.brandDomain}>{item.brandDomain}</div>
                      </td>
                      <td>
                        <span className="module-tag">{(summary && summary.techniques && summary.techniques[item.technique]) || item.technique}</span>
                      </td>
                      <td>
                        {item.registration && item.registration.date ? (
                          <>
                            <div style={{ fontSize: "12px" }}>{formatDate(item.registration.date)}</div>
                            <div className="brand-muted">{ageText(item.registration.date)}</div>
                          </>
                        ) : (
                          <span className="brand-muted">Unknown</span>
                        )}
                      </td>
                      <td><Signals item={item} /></td>
                      <td>
                        <span className={`status-pill ${STATUS_PILL[item.status] || "st-new"}`}>{STATUS_LABELS[item.status] || item.status}</span>
                      </td>
                      <td>
                        <span style={{ color: item.owner ? "var(--fg-primary)" : "var(--fg-tertiary)", fontSize: "12px" }}>{item.owner || "Unassigned"}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="findings-pagination">
            <div className="pagination-left">
              <span>Show</span>
              <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>
                entries · Showing {total > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total)} of {total} look-alike domains
              </span>
            </div>
            <div className="pagination-right">
              <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
                .map((p) => (
                  <button key={p} className={p === page ? "active" : ""} onClick={() => setPage(p)}>
                    {p}
                  </button>
                ))}
              <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">
                Next
              </button>
            </div>
          </div>
          <div className="brand-muted" style={{ padding: "0 20px 14px" }}>
            Only domains generated from your brand names are checked. This is not a list of every newly registered domain.
          </div>
        </div>
      </div>

      {/* Case drawer */}
      {selected && (
        <>
          <div className="findings-drawer-backdrop" onClick={closeDrawer} />
          <aside className="findings-drawer">
            <div className="drawer-header">
              <div className="drawer-title-wrap">
                <span className={`sev-badge sev-${selected.level}`}>
                  <span className="dot" />
                  {LEVEL_LABELS[selected.level]} · {selected.confidence}
                </span>
                <h2 className="drawer-title">{selected.displayDomain || selected.domain}</h2>
              </div>
              <button className="drawer-close-btn" onClick={closeDrawer} aria-label="Close drawer">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="drawer-body">
              {!c ? (
                <div style={{ textAlign: "center", padding: "30px" }}><Spinner color="primary" /></div>
              ) : (
                <>
                  <div className="mgmt-box">
                    <div className="drawer-section-title">Case</div>
                    {!canWrite && <div className="brand-muted" style={{ marginBottom: 6 }}>🔒 You have read-only permissions for brand protection.</div>}
                    <div className="form-row">
                      <label>Status</label>
                      <select value={statusVal} disabled={!canWrite || saving} onChange={(e) => setStatusVal(e.target.value)}>
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      <textarea
                        placeholder="Note (optional): what you found or what you did"
                        value={noteVal}
                        maxLength={500}
                        disabled={!canWrite || saving}
                        onChange={(e) => setNoteVal(e.target.value)}
                      />
                      <div className="mgmt-actions">
                        <Button
                          color="primary"
                          size="sm"
                          disabled={!canWrite || saving || (statusVal === c.status && !noteVal.trim())}
                          onClick={() => patchCase({ ...(statusVal !== c.status ? { status: statusVal } : {}), ...(noteVal.trim() ? { note: noteVal.trim() } : {}) }, "Case updated.")}
                        >
                          {saving ? <Spinner size="sm" /> : "Save"}
                        </Button>
                      </div>
                    </div>
                    <div className="form-row" style={{ marginTop: 6, borderTop: "1px solid var(--border-default)", paddingTop: 12 }}>
                      <label>Owner (a user of your organization)</label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <input type="email" placeholder="analyst@yourcompany.com" value={ownerVal} disabled={!canWrite || saving} onChange={(e) => setOwnerVal(e.target.value)} />
                        <Button color="primary" size="sm" disabled={!canWrite || saving || ownerVal.trim() === (c.owner || "")} onClick={() => patchCase({ owner: ownerVal.trim() }, ownerVal.trim() ? "Owner assigned." : "Owner cleared.")}>
                          {c.owner && !ownerVal.trim() ? "Clear" : "Assign"}
                        </Button>
                      </div>
                    </div>
                    <div className="form-row" style={{ marginTop: 6, borderTop: "1px solid var(--border-default)", paddingTop: 12 }}>
                      <label>Response deadline (for a takedown request)</label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <input type="date" value={dueVal} disabled={!canWrite || saving} onChange={(e) => setDueVal(e.target.value)} />
                        <Button color="primary" size="sm" disabled={!canWrite || saving || dueVal === (c.dueDate ? new Date(c.dueDate).toISOString().slice(0, 10) : "")} onClick={() => patchCase({ dueDate: dueVal || null }, "Deadline saved.")}>
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="drawer-section-title">Why this confidence ({c.confidence} / 100)</div>
                    <div className="brand-reasons">
                      {c.reasons.map((r) => (
                        <div className="brand-reason" key={r.label}>
                          <span>{r.label}</span>
                          <span className="brand-reason-pts">+{r.points}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="drawer-section-title">Evidence</div>
                    <div className="meta-grid">
                      <div className="meta-item"><div className="meta-k">Imitates</div><div className="meta-v">{c.brandDomain}</div></div>
                      <div className="meta-item"><div className="meta-k">Technique</div><div className="meta-v">{(summary && summary.techniques && summary.techniques[c.technique]) || c.technique}</div></div>
                      <div className="meta-item"><div className="meta-k">Registered</div><div className="meta-v">{c.registration && c.registration.date ? `${formatDate(c.registration.date)} (${ageText(c.registration.date)})` : "Unknown"}</div></div>
                      <div className="meta-item"><div className="meta-k">Registrar</div><div className="meta-v">{(c.registration && c.registration.registrar) || "Unknown"}</div></div>
                      <div className="meta-item"><div className="meta-k">Abuse contact</div><div className="meta-v">{(c.registration && c.registration.abuseEmail) || "Not published"}</div></div>
                      <div className="meta-item"><div className="meta-k">IP address</div><div className="meta-v">{(c.dns && c.dns.a && c.dns.a.join(", ")) || "None (not live)"}</div></div>
                      <div className="meta-item"><div className="meta-k">Mail server</div><div className="meta-v">{(c.dns && c.dns.mx && c.dns.mx.length) ? c.dns.mx.join(", ") : "None"}</div></div>
                      <div className="meta-item"><div className="meta-k">Nameservers</div><div className="meta-v">{(c.dns && c.dns.ns && c.dns.ns.join(", ")) || "—"}</div></div>
                      <div className="meta-item"><div className="meta-k">Certificate</div><div className="meta-v">{c.certificate ? `${c.certificate.issuer || "Unknown issuer"}${c.certificate.namesBrand ? " (names the brand)" : ""}` : "None"}</div></div>
                      <div className="meta-item"><div className="meta-k">Landing page</div><div className="meta-v">{c.http ? `${c.http.status || ""} ${c.http.title || "(no title)"}${c.http.hasLoginForm ? " · has a login form" : ""}` : "Not checked"}</div></div>
                      <div className="meta-item"><div className="meta-k">First seen</div><div className="meta-v">{formatDateTime(c.firstSeen)}</div></div>
                      <div className="meta-item"><div className="meta-k">Last seen</div><div className="meta-v">{formatDateTime(c.lastSeen)} · {c.seenCount}×</div></div>
                    </div>
                  </div>

                  {detail.related && detail.related.length > 0 && (
                    <div>
                      <div className="drawer-section-title">Repeat offender signals</div>
                      <div className="brand-reasons">
                        {detail.related.map((r) => (
                          <div className="brand-reason" key={r.domain}>
                            <span>{r.displayDomain || r.domain}</span>
                            <span className="brand-muted">same {r.sharedWith.join(" and ")}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="drawer-section-title">Takedown evidence</div>
                    <div className="brand-muted" style={{ marginBottom: 8 }}>
                      A package for the registrar, host or platform abuse desk. A takedown is not guaranteed; the registrar, host or platform decides.
                    </div>
                    <Button color="primary" outline size="sm" onClick={downloadEvidence}>Download evidence package</Button>
                  </div>

                  {c.notes && c.notes.length > 0 && (
                    <div>
                      <div className="drawer-section-title">Notes</div>
                      <div className="history-timeline">
                        {c.notes.map((n, i) => (
                          <div className="timeline-item" key={i}>
                            <div className="timeline-header"><span className="timeline-change">{n.by}</span><span className="timeline-time">{formatDateTime(n.at)}</span></div>
                            <div className="timeline-note">{n.text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="drawer-section-title">Activity & History</div>
                    <div className="history-timeline">
                      {c.history.map((h, i) => (
                        <div className="timeline-item" key={i}>
                          <div className="timeline-header">
                            <span className="timeline-change">{h.from ? `${STATUS_LABELS[h.from] || h.from} → ${STATUS_LABELS[h.to] || h.to}` : `Created as ${STATUS_LABELS[h.to] || h.to}`}</span>
                            <span className="timeline-time">{formatDateTime(h.at)}</span>
                          </div>
                          <div className="timeline-by">By {h.by || "system"}</div>
                          {h.note && <div className="timeline-note">{h.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default BrandProtection;
